import React, { useState } from "react";
import { LogOut, Pencil, Globe, Bell, Info, ShieldQuestion, Heart, MapPin, FileText, UserPlus, MessageCircle, Download, Trash2, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import IndoBlitzTag, { IndoBlitzFooter } from "../components/IndoBlitz";
import TermsSheet from "../components/TermsSheet";
import PrivacySheet from "../components/PrivacySheet";
import TrustSheet from "../components/TrustSheet";
import HowSosSheet from "../components/HowSosSheet";
import AboutSheet from "../components/AboutSheet";
import { AARYA_WA_NUMBER } from "../constants/whatsapp";

export default function Profile({ onJump }) {
  const { user, logout, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const [howSosOpen, setHowSosOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waMsg, setWaMsg] = useState("");
  const [diagBusy, setDiagBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [gdprBusy, setGdprBusy] = useState(false);
  const [gdprMsg, setGdprMsg] = useState("");

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try { await updateProfile({ name: name.trim(), email: email.trim() || null }); setEditing(false); }
    finally { setSaving(false); }
  };

  const sendWhatsAppTest = async () => {
    if (!user?.phone) return;
    setWaTesting(true);
    setWaMsg("");
    try {
      const now = new Date();
      const stamp = `${now.toLocaleDateString(undefined, { day: "2-digit", month: "short" })} ${now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
      const { data } = await api.post("/whatsapp/test-send", {
        to_phone: user.phone,
        template_name: "aarya_safe_notification",
        variables: [user?.name || "Someone", stamp],
      });
      if (!data.ok) {
        const errText = (data.error || "").toLowerCase();
        const inReview = errText.includes("in review") || errText.includes("pending");
        setWaMsg(inReview
          ? `⏳ Template still in review by Meta. Try again in ~1 hour.`
          : `✗ ${data.error || "Send failed. Check Render logs."}`);
        return;
      }

      // Meta returned 200 (accepted). Delivery is asynchronous - poll status.
      const msgId = data.message_id;
      const missingWaId = !data.wa_id;
      setWaMsg(missingWaId
        ? `⚠ Accepted by Meta but no wa_id returned. Recipient is likely NOT on your WABA allowed-recipient list (dev mode). See instructions below.`
        : `↗ Accepted by Meta (id: ${msgId?.slice(-10) || "?"}). Checking delivery…`);

      if (!msgId) return;

      // Poll delivery status every 3s for up to 30s.
      const deadline = Date.now() + 30_000;
      const poll = async () => {
        if (Date.now() > deadline) {
          setWaMsg((prev) => prev + `\n\n⏱ No delivery confirmation after 30s. If WhatsApp did not arrive:\n1) Add ${user.phone} under Business Manager → WhatsApp → API Setup → 'To' allowed recipients (WABA is in dev mode until fully verified).\n2) Confirm ${user.phone} has WhatsApp installed and business messages are NOT muted.\n3) Confirm the webhook is configured in Meta (Configuration → Webhooks) so we can capture delivery events.`);
          return;
        }
        try {
          const { data: s } = await api.get(`/whatsapp/status/${encodeURIComponent(msgId)}`);
          const status = s.delivery_status;
          if (status === "delivered" || status === "read") {
            setWaMsg(`✓ WhatsApp ${status} to ${user.phone}. Check WhatsApp now.`);
            return;
          }
          if (status === "failed") {
            setWaMsg(`✗ Meta reported delivery FAILED: ${s.delivery_error || "no reason given"}. Common cause: recipient not on WABA allowed list.`);
            return;
          }
          if (status === "sent") {
            setWaMsg(`↗ Meta says 'sent' - waiting for delivery confirmation from ${user.phone}'s WhatsApp…`);
          }
          setTimeout(poll, 3000);
        } catch (e) {
          // Non-fatal; keep polling.
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setWaMsg(`✗ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setWaTesting(false);
    }
  };

  const registerCloudApi = async () => {
    const pin = window.prompt("Enter a 6-digit PIN for the Aarya sender number (save it - Meta may ask you to re-enter it during future re-verifications):");
    if (!pin) return;
    setWaTesting(true);
    setWaMsg("");
    try {
      const { data } = await api.post("/whatsapp/register-cloud-api", { pin });
      if (data.ok) {
        setWaMsg("✓ Cloud API registered. Now tap Send test.");
      } else {
        setWaMsg(`✗ ${data.error || "Registration failed."}`);
      }
    } catch (e) {
      setWaMsg(`✗ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setWaTesting(false);
    }
  };

  const runDiagnostic = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.get("/whatsapp/diagnose");
      setDiag(data);
    } catch (e) {
      setDiag({
        summary: e?.response?.status === 403
          ? "❌ Founder-only endpoint. Your phone number is not on the FOUNDER_PHONES list in Render Environment."
          : `❌ Diagnostic failed: ${e?.response?.data?.detail || e.message}`,
        checks: [],
        next_actions: [],
      });
    } finally {
      setDiagBusy(false);
    }
  };

  const subscribeWaba = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.post("/whatsapp/subscribe-waba");
      if (data.ok) {
        setWaMsg(`✓ WABA subscribed to Meta App. Now send a WhatsApp message from your phone to +91 92702 95963 and check Render logs - the webhook should fire.`);
      } else {
        setWaMsg(`✗ WABA subscribe failed: ${data.error}`);
      }
    } catch (e) {
      setWaMsg(`✗ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDiagBusy(false);
    }
  };

  const exportMyData = async () => {
    setGdprBusy(true);
    setGdprMsg("");
    try {
      const res = await api.get("/me/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aarya-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setGdprMsg("✓ Your data was downloaded. Keep the file private.");
    } catch (e) {
      setGdprMsg(`✗ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGdprBusy(false);
    }
  };

  const deleteMyAccount = async () => {
    const first = window.confirm(
      "This will PERMANENTLY delete your Aarya account, your trusted circle, every event, and every notification we ever sent for you. It cannot be undone.\n\nAre you absolutely sure?"
    );
    if (!first) return;
    const phrase = window.prompt('To confirm, type exactly: DELETE MY AARYA ACCOUNT');
    if (!phrase) return;
    setGdprBusy(true);
    setGdprMsg("");
    try {
      const { data } = await api.delete("/me", { data: { confirm_phrase: phrase } });
      if (data.ok) {
        setGdprMsg("✓ Account deleted. Logging you out…");
        setTimeout(() => logout(), 1500);
      } else {
        setGdprMsg("✗ Deletion failed. Please contact admin@indoblitz.com.");
      }
    } catch (e) {
      setGdprMsg(`✗ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGdprBusy(false);
    }
  };

  return (
    <div className="px-6 pt-4 pb-4" data-testid="profile-screen">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[12px] tracking-[0.16em] uppercase text-ink-500">Your</p>
          <h1 className="font-display text-[34px] leading-[1.05] text-ink-900 mt-1 tracking-tight">Profile</h1>
        </div>
        <IndoBlitzTag/>
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-sand-200 p-5" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-sage-500 text-white flex items-center justify-center font-display text-2xl flex-shrink-0">
            {(user?.name || "?").charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="space-y-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
                  data-testid="profile-name-input"
                  className="w-full h-11 px-3 rounded-xl border-2 border-sand-200 outline-none focus:border-sage-500"/>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                  data-testid="profile-email-input"
                  className="w-full h-11 px-3 rounded-xl border-2 border-sand-200 outline-none focus:border-sage-500"/>
                <button onClick={save} disabled={saving} data-testid="profile-save"
                  className="w-full h-11 rounded-xl bg-sage-500 text-white font-semibold disabled:opacity-50">Save</button>
              </div>
            ) : (
              <>
                <p className="font-semibold text-ink-900 truncate">{user?.name || "Add your name"}</p>
                <p className="text-[13px] text-ink-700 truncate">{user?.email || <span className="text-ink-300">No email</span>}</p>
                <p className="text-[12px] text-ink-500 truncate">{user?.phone}</p>
                {user?.country_name && (
                  <p className="text-[11px] text-sage-700 mt-1">📍 {user.country_name}</p>
                )}
              </>
            )}
          </div>
          {!editing && (
            <button onClick={() => { setEditing(true); setName(user?.name || ""); setEmail(user?.email || ""); }}
              data-testid="profile-edit"
              className="h-10 w-10 rounded-full bg-sand-100 text-ink-700 flex items-center justify-center"><Pencil size={16}/></button>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-sand-200 divide-y divide-sand-200 overflow-hidden">
        <button onClick={() => onJump && onJump("invite")} data-testid="profile-row-invite"
          className="w-full flex items-center gap-4 px-5 py-4 active:bg-sand-100 text-left">
          <UserPlus size={18} className="text-terracotta-500"/>
          <span className="flex-1 text-[15px] text-ink-900">Invite a loved one</span>
          <span className="text-[13px] text-terracotta-600">↗</span>
        </button>
        {/* Aarya Radar is a founder-only aggregated activation map. Hidden from
            end users - noise for someone who just wants safety alerts. */}
        {user.is_founder && (
        <button onClick={() => onJump && onJump("radar")} data-testid="profile-row-radar"
          className="w-full flex items-center gap-4 px-5 py-4 active:bg-sand-100 text-left">
          <MapPin size={18} className="text-sage-700"/>
          <span className="flex-1 text-[15px] text-ink-900">Aarya Radar (founder)</span>
          <span className="text-[13px] text-ink-500">↗</span>
        </button>
        )}
        <button onClick={() => setTermsOpen(true)} data-testid="profile-row-terms"
          className="w-full flex items-center gap-4 px-5 py-4 active:bg-sand-100 text-left">
          <FileText size={18} className="text-ink-700"/>
          <span className="flex-1 text-[15px] text-ink-900">Terms &amp; Conditions</span>
          <span className="text-[13px] text-ink-500">IndoBlitz</span>
        </button>
        <button onClick={() => setPrivacyOpen(true)} data-testid="profile-row-privacy"
          className="w-full flex items-center gap-4 px-5 py-4 active:bg-sand-100 text-left">
          <Shield size={18} className="text-sage-700"/>
          <span className="flex-1 text-[15px] text-ink-900">Privacy Policy</span>
          <span className="text-[13px] text-ink-500">GDPR &amp; DPDP</span>
        </button>
        <button onClick={() => setTrustOpen(true)} data-testid="profile-row-trust"
          className="w-full flex items-center gap-4 px-5 py-4 active:bg-sand-100 text-left">
          <Heart size={18} className="text-terracotta-600"/>
          <span className="flex-1 text-[15px] text-ink-900">The Aarya promise</span>
          <span className="text-[13px] text-ink-500">Trust</span>
        </button>
        {[
          { Icon: Bell, label: "Daily check-in reminders", right: "9:00 AM", onClick: null },
          { Icon: Globe, label: "Language", right: "English", onClick: null },
          { Icon: ShieldQuestion, label: "How SOS works", right: "Hold 2s", onClick: () => setHowSosOpen(true) },
          { Icon: Info, label: "About Aarya", right: "v1.0", onClick: () => setAboutOpen(true) },
        ].map((row, i) => (
          <button
            key={i}
            onClick={row.onClick || undefined}
            disabled={!row.onClick}
            className={`w-full flex items-center gap-4 px-5 py-4 text-left ${row.onClick ? "active:bg-sand-100" : "opacity-60 cursor-not-allowed"}`}
            data-testid={`profile-row-${i}`}
          >
            <row.Icon size={18} className="text-ink-700"/>
            <span className="flex-1 text-[15px] text-ink-900">{row.label}</span>
            <span className="text-[13px] text-ink-500">{row.onClick ? row.right : "Coming soon"}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 bg-terracotta-50 border border-terracotta-400/30 rounded-3xl p-5" data-testid="profile-demo-info">
        <p className="text-[11px] tracking-[0.16em] uppercase text-terracotta-600 font-semibold">Free forever, basics for all.</p>
        <p className="text-[14px] text-ink-900 mt-1 leading-snug">
          The core safety net (Trusted Circle, I-am-Safe, Silent SOS, Daily Check-in) is free for everyone, everywhere. Optional premium add-ons (extended live tracking, larger circle, evidence locker) are paid for all users worldwide. Essential safety is never behind a paywall.
        </p>
      </div>

      {/* Personal WhatsApp opt-in card - only meaningful once logged in because
          the pre-filled message must embed the user's registered phone so the
          webhook can verify the sender matches the account. */}
      <PersonalWaOptInCard phone={user.phone}/>

      {/* Founder-only diagnostic card. Hidden from end users. `is_founder` is
          set server-side (GET /auth/me) based on whether the phone is on the
          FOUNDER_PHONES env list - end users never see any of this. */}
      {user.is_founder && (
      <div className="mt-6 bg-white rounded-3xl border border-sand-200 p-5" data-testid="profile-wa-test">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={18} className="text-sage-700"/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-ink-900">Founder tools - WhatsApp diagnostics</p>
            <p className="text-[12px] text-ink-500 leading-snug mt-0.5">
              Sends the Aarya safe-notification template to your own number to verify delivery.
            </p>
          </div>
        </div>
        <button onClick={sendWhatsAppTest} disabled={waTesting} data-testid="wa-test-button"
          className="mt-3 w-full h-10 rounded-xl bg-sage-500 text-white text-[14px] font-semibold disabled:opacity-50">
          {waTesting ? "Sending…" : `Send test to ${user?.phone || "your number"}`}
        </button>
        <button onClick={registerCloudApi} disabled={waTesting} data-testid="wa-register-button"
          className="mt-2 w-full h-9 rounded-xl bg-sand-100 text-ink-700 text-[12px] font-medium disabled:opacity-50">
          One-time: Register Cloud API sender
        </button>
        <button onClick={runDiagnostic} disabled={diagBusy} data-testid="wa-diagnose-button"
          className="mt-2 w-full h-9 rounded-xl bg-terracotta-100 text-terracotta-700 text-[12px] font-medium disabled:opacity-50">
          {diagBusy ? "Diagnosing…" : "Run WhatsApp Diagnostic"}
        </button>
        <button onClick={subscribeWaba} disabled={diagBusy} data-testid="wa-subscribe-waba-button"
          className="mt-2 w-full h-9 rounded-xl bg-indigo-100 text-indigo-700 text-[12px] font-medium disabled:opacity-50">
          Subscribe WABA to Meta App (one-time)
        </button>
        {waMsg && (
          <p className="mt-2 text-[12px] text-ink-700 leading-snug whitespace-pre-line" data-testid="wa-test-result">{waMsg}</p>
        )}
        {diag && (
          <div className="mt-3 bg-sand-50 rounded-xl border border-sand-200 p-3 text-[12px]" data-testid="wa-diagnose-result">
            <p className="font-semibold text-ink-900 leading-snug whitespace-pre-line mb-2">{diag.summary}</p>
            <div className="space-y-1.5 mb-3">
              {diag.checks?.map((c, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="mt-0.5">{c.status === "ok" ? "OK" : c.status === "warn" ? "!" : "X"}</span>
                  <span className="flex-1 text-ink-700 leading-snug">{c.label}</span>
                </div>
              ))}
            </div>
            {diag.next_actions?.length > 0 && (
              <div className="border-t border-sand-200 pt-2">
                <p className="font-semibold text-ink-900 mb-1">Next actions:</p>
                <ol className="list-decimal list-inside text-ink-700 space-y-0.5">
                  {diag.next_actions.map((a, i) => <li key={i} className="leading-snug">{a}</li>)}
                </ol>
              </div>
            )}
            <details className="mt-2">
              <summary className="text-ink-500 cursor-pointer">Full technical details (for developers)</summary>
              <pre className="mt-1 text-[10px] bg-white p-2 rounded overflow-auto max-h-64">{JSON.stringify(diag, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>
      )}

      <div className="mt-6 bg-white rounded-3xl border border-sand-200 p-5" data-testid="profile-gdpr">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-sand-100 flex items-center justify-center flex-shrink-0">
            <Download size={18} className="text-ink-700"/>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-ink-900">Your data, your right</p>
            <p className="text-[12px] text-ink-500 leading-snug mt-0.5">
              Aarya complies with GDPR &amp; India&apos;s DPDP. Download everything we hold about you, or delete your account permanently.
            </p>
          </div>
        </div>
        <button onClick={exportMyData} disabled={gdprBusy} data-testid="gdpr-export-button"
          className="mt-3 w-full h-10 rounded-xl bg-sand-100 text-ink-900 text-[14px] font-medium disabled:opacity-50">
          Download my data (JSON)
        </button>
        <button onClick={deleteMyAccount} disabled={gdprBusy} data-testid="gdpr-delete-button"
          className="mt-2 w-full h-10 rounded-xl bg-white border border-terracotta-400/50 text-terracotta-600 text-[13px] font-medium disabled:opacity-50 flex items-center justify-center gap-2">
          <Trash2 size={14}/> Delete my Aarya account
        </button>
        {gdprMsg && (
          <p className="mt-2 text-[12px] text-ink-700 leading-snug" data-testid="gdpr-result">{gdprMsg}</p>
        )}
      </div>

      <button onClick={logout} data-testid="logout-button"
        className="mt-6 w-full h-[58px] rounded-full bg-ink-900 text-white font-semibold flex items-center justify-center gap-2">
        <LogOut size={18}/> Sign out
      </button>

      <div className="mt-8">
        <IndoBlitzFooter/>
      </div>

      <TermsSheet open={termsOpen} onClose={() => setTermsOpen(false)}/>
      <PrivacySheet open={privacyOpen} onClose={() => setPrivacyOpen(false)}/>
      <TrustSheet open={trustOpen} onClose={() => setTrustOpen(false)}/>
      <HowSosSheet open={howSosOpen} onClose={() => setHowSosOpen(false)}/>
      <AboutSheet open={aboutOpen} onClose={() => setAboutOpen(false)}/>
    </div>
  );
}

// Personal WhatsApp opt-in card. Only makes sense post-login because the
// pre-filled message includes the user's registered phone number - this lets
// the webhook verify that whichever WhatsApp account actually sent the message
// belongs to the same phone the user signed up with. Dual-SIM users get a
// clear warning to switch WhatsApp accounts before tapping.
function PersonalWaOptInCard({ phone }) {
  if (!phone) return null;
  const msg = `Hi Aarya, please activate WhatsApp alerts for my Aarya account. My-Phone: ${phone}`;
  const waUrl = `https://wa.me/${AARYA_WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  return (
    <div className="mt-6 bg-white rounded-3xl border border-emerald-200 p-5" data-testid="profile-wa-optin">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <MessageCircle size={18} className="text-emerald-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-ink-900">Enable WhatsApp alerts</p>
          <p className="text-[12px] text-ink-500 leading-snug mt-0.5">
            Get Aarya safety alerts on WhatsApp instead of SMS. Free, forever.
          </p>
        </div>
      </div>
      <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-snug">
        <strong>Dual-SIM phone?</strong> WhatsApp opens whichever account is currently active on your
        device. Please make sure you&apos;re signed in to WhatsApp on <strong>{phone}</strong> before
        tapping - Aarya will verify the sender matches this number and warn you if it doesn&apos;t.
      </div>
      <a
        href={waUrl}
        target="_blank"
        rel="noreferrer noopener"
        data-testid="profile-wa-optin-btn"
        className="mt-3 w-full h-[52px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
      >
        <MessageCircle size={18}/>
        Enable WhatsApp alerts (one tap)
      </a>
    </div>
  );
}
